<script setup>
/**
 * 上映スケジュールAI ステップ1：劇場特性・スクリーン設定（DEV-14981）
 *
 * - 劇場基本情報：BigQuery 由来の読み取り専用
 * - スクリーン設定：追加 / 削除 / 編集 / 並び替え。上映可能時間帯の終了は 30:00 まで（翌朝 6 時）
 * - 上映可能バージョン：2D/3D・上映方式・特殊をそれぞれ複数選択（項目内はいずれか、項目間はすべてを満たす）
 * - 清掃間隔：スクリーンごとの既定値（タイムチャート作成で期間を初めて保存するときの初期値になる）
 * - 経営戦略：自由記述（各 2000 文字まで、空欄可）
 * 保存は経営戦略とスクリーンで別々に PUT する（それぞれの見出しに保存ボタン）。対象劇場はバックエンドがログインアカウントから決める。
 * GET の saved は「スクリーンが保存済みか」を表す（経営戦略だけ保存した場合は saved=false のまま）
 */
import {
  ref, computed, onMounted, nextTick,
} from "vue";
import { onBeforeRouteLeave } from "vue-router";
import { ElMessage, ElMessageBox } from "element-plus";
import {
  getScheduleAiSettingService,
  saveScheduleAiSettingStrategyService,
  saveScheduleAiSettingScreensService,
  getScheduleAiSettingOptionsService,
} from "@/api/dp/index.js";

// 座席数の上下限
const SEATS_MIN = 1;
const SEATS_MAX = 9999;
// 清掃間隔（分）の上限と、未設定の旧データに使う既定値（バックエンドと同じ値）
const CLEANING_MAX = 120;
const DEFAULT_CLEANING_MINUTE = 15;
// 自由記述の文字数上限
const TEXT_MAX = 2000;
// 終了時刻の上限（30:00 = 1800 分）
const TIME_MAX_MINUTES = 30 * 60;
// 開始時刻の上限（24:00 未満 = 当日中に開始）
const OPEN_TIME_LIMIT_MINUTES = 24 * 60;
// HH:mm 形式（00:00〜30:59 まで形式としては許容し、30:00 超過は別途チェック）
const TIME_REG = /^([0-2]\d|30):([0-5]\d)$/;
// 時刻プルダウンの刻み（分）。リストにない時刻も手入力で追加できる
const TIME_STEP = 15;

// 経営戦略の入力欄定義（キーは API のフィールド名）
const STRATEGY_FIELDS = [
  { key: "strengthText", label: "劇場の強み", placeholder: "例：駅直結でアクセスが良い、大型スクリーンを備えている など" },
  { key: "audienceText", label: "ターゲット客層", placeholder: "例：ファミリー層、近隣大学の学生 など" },
  { key: "peakTimeText", label: "強い時間帯", placeholder: "例：平日夜のレイトショー、週末の午前 など" },
  { key: "strategyText", label: "経営戦略", placeholder: "例：話題作を大スクリーンで回数多く上映する など" },
];

// 時刻プルダウン候補（00:00〜30:00）
const TIME_OPTIONS = (() => {
  const list = [];
  for (let m = 0; m <= TIME_MAX_MINUTES; m += TIME_STEP) {
    list.push(`${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`);
  }
  return list;
})();
// 開始時刻プルダウン候補（00:00〜23:45。24:00 以降の開始は不可）
const OPEN_TIME_OPTIONS = TIME_OPTIONS.filter((t) => t < "24:00");

const loading = ref(false);
// 保存中フラグ（経営戦略・スクリーンで別々に持つ）
const savingStrategy = ref(false);
const savingScreens = ref(false);
// 取得失敗時のメッセージ（劇場未紐付けアカウントなど）。値があればフォームを出さない
const loadErrorMsg = ref("");
const saved = ref(false);
const cinema = ref(null);
const formRef = ref(null);
const form = ref({
  strategy: {},
  screens: [],
});
const options = ref({
  dimensions: [],
  screenFormats: [],
  specials: [],
});
// 未保存変更の検知用スナップショット（送信用データの JSON。経営戦略・スクリーンで別々に持つ）
const strategySnapshot = ref("");
const screensSnapshot = ref("");

// v-for のキー用ローカル連番（screenId は新規追加時 null のため使えない）
let localKeySeq = 0;
const nextKey = () => {
  localKeySeq += 1;
  return `screen-${localKeySeq}`;
};

/**
 * "HH:mm" を分に変換。形式不正なら null
 */
const toMinutes = (value) => {
  if (typeof value !== "string" || !TIME_REG.test(value)) return null;
  const [h, m] = value.split(":").map(Number);
  return h * 60 + m;
};

/**
 * 文字列配列に正規化（null・重複・空文字を除く）
 */
const toNameList = (list) => (Array.isArray(list)
  ? [...new Set(list.filter((v) => typeof v === "string" && v))]
  : []);

/**
 * API のスクリーンデータを画面編集用に変換
 * 上映可能バージョンは属性ごとの名称配列（dimensions・screenFormats は必須、specials は空可）
 */
const toEditScreen = (s = {}) => ({
  _key: nextKey(),
  screenId: s.screenId ?? null,
  sourceScreenId: s.sourceScreenId ?? null,
  name: s.name ?? "",
  maxSeats: s.maxSeats ?? undefined,
  openTime: s.openTime ?? "",
  closeTime: s.closeTime ?? "",
  cleaningMinute: s.cleaningMinute ?? DEFAULT_CLEANING_MINUTE,
  dimensions: toNameList(s.dimensions),
  screenFormats: toNameList(s.screenFormats),
  specials: toNameList(s.specials),
});

/**
 * 経営戦略の PUT 用リクエストボディ（空欄は null で送る）
 */
const buildStrategyPayload = () => STRATEGY_FIELDS.reduce((acc, { key }) => {
  const text = form.value.strategy[key];
  acc[key] = text && text.trim() ? text : null;
  return acc;
}, {});

/**
 * スクリーンの PUT 用リクエストボディ（配列順が表示順。sort も表示順で振り直す）
 */
const buildScreensPayload = () => form.value.screens.map((s, index) => ({
  screenId: s.screenId,
  sourceScreenId: s.sourceScreenId,
  name: (s.name || "").trim(),
  maxSeats: s.maxSeats,
  openTime: s.openTime,
  closeTime: s.closeTime,
  cleaningMinute: s.cleaningMinute,
  sort: index + 1,
  dimensions: [...s.dimensions],
  screenFormats: [...s.screenFormats],
  specials: [...s.specials],
}));

const isStrategyDirty = computed(
  () => !!strategySnapshot.value && JSON.stringify(buildStrategyPayload()) !== strategySnapshot.value,
);
const isScreensDirty = computed(
  () => !!screensSnapshot.value && JSON.stringify(buildScreensPayload()) !== screensSnapshot.value,
);
const isDirty = computed(() => isStrategyDirty.value || isScreensDirty.value);

// 総座席数（未入力・不正値は 0 として数える）
const totalSeats = computed(() => form.value.screens.reduce(
  (sum, s) => sum + (Number.isInteger(s.maxSeats) ? s.maxSeats : 0),
  0,
));

// ==================== 営業時間ボード ====================
// 表示範囲は通常 06:00〜30:00。06:00 より早く開始するスクリーンがあれば、その時刻（時単位で切り捨て）まで広げる
const BOARD_DEFAULT_START = 6 * 60;
// 目盛りの間隔（分）
const BOARD_TICK_STEP = 3 * 60;
const NEXT_DAY_MINUTES = 24 * 60;

const boardStart = computed(() => {
  const opens = form.value.screens
    .map((s) => toMinutes(s.openTime))
    .filter((m) => m !== null && m < OPEN_TIME_LIMIT_MINUTES);
  const earliest = opens.length ? Math.min(...opens) : BOARD_DEFAULT_START;
  return Math.min(BOARD_DEFAULT_START, Math.floor(earliest / 60) * 60);
});

/**
 * 分 → ボード上の左端からの位置（%）
 */
const toBoardPercent = (minutes) => ((minutes - boardStart.value) / (TIME_MAX_MINUTES - boardStart.value)) * 100;

// 目盛り（3 時間刻み）。ラベルは時のみ（24 以降もそのまま 27・30 と表記）
const boardTicks = computed(() => {
  const ticks = [];
  const first = Math.ceil(boardStart.value / BOARD_TICK_STEP) * BOARD_TICK_STEP;
  for (let m = first; m <= TIME_MAX_MINUTES; m += BOARD_TICK_STEP) {
    ticks.push({ minutes: m, label: String(m / 60).padStart(2, "0"), left: toBoardPercent(m) });
  }
  return ticks;
});

// 24:00 以降（翌日）帯の開始位置
const nextDayLeft = computed(() => toBoardPercent(NEXT_DAY_MINUTES));

/**
 * スクリーンの営業時間バーの位置。時刻が未入力・不正・前後逆転のときは null（「時間未設定」表示）
 */
const barOf = (screen) => {
  const open = toMinutes(screen.openTime);
  const close = toMinutes(screen.closeTime);
  if (open === null || close === null) return null;
  if (open >= OPEN_TIME_LIMIT_MINUTES || close > TIME_MAX_MINUTES || open >= close) return null;
  const left = toBoardPercent(open);
  return { left: `${left}%`, width: `${toBoardPercent(close) - left}%` };
};

/**
 * ボードの行をクリックしたら該当スクリーンの設定行へスクロールし、スクリーン名にフォーカス
 */
const focusScreen = (screen) => {
  const el = document.getElementById(`screen-${screen._key}`);
  if (!el) return;
  const reduceMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
  el.scrollIntoView({ behavior: reduceMotion ? "auto" : "smooth", block: "center" });
  el.querySelector("input")?.focus({ preventScroll: true });
};

/**
 * 取得データをフォームへ反映し、スナップショットを取り直す
 * keepStrategy=true のときは経営戦略の入力中の内容を残す（スクリーン保存後の取り直し用）
 */
const applyData = (data = {}, { keepStrategy = false } = {}) => {
  saved.value = !!data.saved;
  cinema.value = data.cinema || null;
  const strategy = data.strategy || {};
  form.value = {
    strategy: keepStrategy
      ? form.value.strategy
      : STRATEGY_FIELDS.reduce((acc, { key }) => {
        acc[key] = strategy[key] ?? "";
        return acc;
      }, {}),
    screens: (data.screens || [])
      .slice()
      .sort((a, b) => (a.sort ?? 0) - (b.sort ?? 0))
      .map(toEditScreen),
  };
  if (!keepStrategy) strategySnapshot.value = JSON.stringify(buildStrategyPayload());
  screensSnapshot.value = JSON.stringify(buildScreensPayload());
  nextTick(() => formRef.value?.clearValidate());
};

const fetchSetting = async () => {
  loading.value = true;
  loadErrorMsg.value = "";
  try {
    const [settingRes, optionRes] = await Promise.all([
      getScheduleAiSettingService(),
      getScheduleAiSettingOptionsService(),
    ]);
    const opt = optionRes?.data || {};
    options.value = {
      dimensions: opt.dimensions || [],
      screenFormats: opt.screenFormats || [],
      specials: opt.specials || [],
    };
    applyData(settingRes?.data || {});
  } catch (e) {
    // code=500 のメッセージは request.js 側でトースト済み。画面にも残しておく
    loadErrorMsg.value = e?.message || "設定の取得に失敗しました";
  } finally {
    loading.value = false;
  }
};

// ==================== スクリーン操作 ====================

const addScreen = () => {
  form.value.screens.push(toEditScreen());
};

const removeScreen = async (index) => {
  const screen = form.value.screens[index];
  try {
    await ElMessageBox.confirm(
      `「${screen.name || `スクリーン${index + 1}`}」を削除しますか？（保存するまで確定しません）`,
      "確認",
      { confirmButtonText: "削除", cancelButtonText: "キャンセル", type: "warning" },
    );
  } catch {
    return;
  }
  form.value.screens.splice(index, 1);
  // 削除で添字がずれるため、残ったエラー表示はリセットする
  nextTick(() => formRef.value?.clearValidate());
};

/**
 * 並び替え（direction: -1 上へ / 1 下へ）
 */
const moveScreen = (index, direction) => {
  const target = index + direction;
  const list = form.value.screens;
  if (target < 0 || target >= list.length) return;
  [list[index], list[target]] = [list[target], list[index]];
  nextTick(() => formRef.value?.clearValidate());
};

// ==================== バリデーション ====================
// 各ルールに required: true を持たせる。持たせないと el-form-item の required 属性がメッセージなしの必須ルールを自動追加し、
// blur 時に Element Plus 既定の英語メッセージ（例「screens.0.maxSeats is required」）が出てしまう

const screenLabel = (index) => `スクリーン${index + 1}`;

const nameRule = (index) => ({
  required: true,
  validator: (_rule, value, callback) => {
    const name = (value || "").trim();
    if (!name) return callback(new Error(`${screenLabel(index)}：スクリーン名を入力してください`));
    const duplicated = form.value.screens.some(
      (s, i) => i !== index && (s.name || "").trim() === name,
    );
    if (duplicated) return callback(new Error(`${screenLabel(index)}：スクリーン名「${name}」が重複しています`));
    return callback();
  },
  trigger: "blur",
});

const seatsRule = (index) => ({
  required: true,
  validator: (_rule, value, callback) => {
    if (value === undefined || value === null || value === "") {
      return callback(new Error(`${screenLabel(index)}：最大座席数を入力してください`));
    }
    if (!Number.isInteger(value) || value < SEATS_MIN || value > SEATS_MAX) {
      return callback(new Error(`${screenLabel(index)}：最大座席数は${SEATS_MIN}〜${SEATS_MAX}の整数で入力してください`));
    }
    return callback();
  },
  trigger: "change",
});

const openTimeRule = (index) => ({
  required: true,
  validator: (_rule, value, callback) => {
    if (!value) return callback(new Error(`${screenLabel(index)}：開始時刻を入力してください`));
    const minutes = toMinutes(value);
    if (minutes === null) return callback(new Error(`${screenLabel(index)}：開始時刻は HH:mm 形式で入力してください`));
    if (minutes >= OPEN_TIME_LIMIT_MINUTES) {
      return callback(new Error(`${screenLabel(index)}：上映開始時刻は00:00〜23:59で入力してください`));
    }
    return callback();
  },
  trigger: "change",
});

const closeTimeRule = (index) => ({
  required: true,
  validator: (_rule, value, callback) => {
    if (!value) return callback(new Error(`${screenLabel(index)}：終了時刻を入力してください`));
    const minutes = toMinutes(value);
    if (minutes === null) return callback(new Error(`${screenLabel(index)}：終了時刻は HH:mm 形式で入力してください`));
    if (minutes > TIME_MAX_MINUTES) return callback(new Error(`${screenLabel(index)}：終了時刻は30:00以前で入力してください`));
    const open = toMinutes(form.value.screens[index]?.openTime);
    if (open !== null && open >= minutes) {
      return callback(new Error(`${screenLabel(index)}：終了時刻は開始時刻より後にしてください`));
    }
    return callback();
  },
  trigger: "change",
});

const cleaningRule = (index) => ({
  required: true,
  validator: (_rule, value, callback) => {
    if (value === undefined || value === null || value === "") {
      return callback(new Error(`${screenLabel(index)}：清掃間隔を入力してください`));
    }
    if (!Number.isInteger(value) || value < 0 || value > CLEANING_MAX) {
      return callback(new Error(`${screenLabel(index)}：清掃間隔は0〜${CLEANING_MAX}分の整数で入力してください`));
    }
    return callback();
  },
  trigger: "change",
});

/**
 * 上映可能バージョンの必須項目（2D/3D・上映方式）は 1 つ以上選択
 */
const capabilityRule = (index, label) => ({
  required: true,
  validator: (_rule, value, callback) => {
    if (!Array.isArray(value) || !value.length) {
      return callback(new Error(`${screenLabel(index)}：上映可能な${label}を1つ以上選択してください`));
    }
    return callback();
  },
  trigger: "change",
});

// 開始時刻を変えたら終了時刻の前後チェックもやり直す
const onOpenTimeChange = (index) => {
  if (form.value.screens[index]?.closeTime) {
    formRef.value?.validateField(`screens.${index}.closeTime`).catch(() => {});
  }
};

// スクリーン名を変えたら他スクリーンの重複エラーも解消されうるので全スクリーン名を再チェック
const onNameBlur = () => {
  const props = form.value.screens.map((_s, i) => `screens.${i}.name`);
  formRef.value?.validateField(props).catch(() => {});
};

// ==================== 保存 ====================

/**
 * 経営戦略だけを保存（スクリーンのバリデーションは行わない）
 */
const handleSaveStrategy = async () => {
  const payload = buildStrategyPayload();
  savingStrategy.value = true;
  try {
    await saveScheduleAiSettingStrategyService(payload);
    ElMessage.success("経営戦略を保存しました");
    // 保存した内容をそのままスナップショットにする（スクリーン側の編集中の内容には触れない）
    strategySnapshot.value = JSON.stringify(payload);
  } catch {
    // エラーメッセージは request.js でトースト済み
  } finally {
    savingStrategy.value = false;
  }
};

/**
 * スクリーンだけを保存（全量・表示順）
 */
const handleSaveScreens = async () => {
  if (!form.value.screens.length) {
    ElMessage.error("スクリーンを1件以上登録してください");
    return;
  }
  try {
    await formRef.value.validate();
  } catch (fields) {
    // 先頭のエラーをトーストで知らせる（該当欄へのスクロールは el-form の scroll-to-error に任せる）
    const first = fields && Object.values(fields)[0]?.[0];
    if (first?.message) ElMessage.error(first.message);
    return;
  }
  savingScreens.value = true;
  try {
    await saveScheduleAiSettingScreensService(buildScreensPayload());
    ElMessage.success("スクリーンを保存しました");
    // 新規スクリーンの screenId を受け取るため取り直す（経営戦略の未保存の入力は残す）
    const res = await getScheduleAiSettingService();
    applyData(res?.data || {}, { keepStrategy: true });
  } catch {
    // エラーメッセージ（何番目のスクリーンのどの項目か）は request.js でトースト済み
  } finally {
    savingScreens.value = false;
  }
};

// 未保存のまま離脱しようとしたら確認する
onBeforeRouteLeave(async () => {
  if (!isDirty.value) return true;
  try {
    await ElMessageBox.confirm("保存されていない変更があります。破棄して移動しますか？", "確認", {
      confirmButtonText: "破棄して移動",
      cancelButtonText: "キャンセル",
      type: "warning",
    });
    return true;
  } catch {
    return false;
  }
});

onMounted(fetchSetting);
</script>

<template>
  <div v-loading="loading" class="setting-page">
    <!-- 上部のタイトルバー（保存ボタンはスクリーン・経営戦略それぞれの見出しに置く） -->
    <header class="page-bar">
      <div class="page-bar__title">
        <h2>劇場特性・スクリーン設定</h2>
        <span v-if="cinema && !saved" class="state state--warn">未保存</span>
        <span v-else-if="isDirty" class="state">変更あり</span>
      </div>
    </header>

    <el-empty v-if="loadErrorMsg && !loading" :description="loadErrorMsg">
      <el-button @click="fetchSetting">再読み込み</el-button>
    </el-empty>

    <template v-else-if="cinema">
      <p v-if="!saved" class="first-notice">
        劇場マスタからスクリーン名と座席数を仮入力しています。内容を確認し、上映可能時間帯とバージョンを入力して保存してください。
      </p>

      <!-- 劇場基本情報（読み取り専用） -->
      <section class="panel cinema-panel">
        <div class="cinema-main">
          <h3 class="cinema-name">{{ cinema.name || "-" }}</h3>
          <p class="cinema-address">
            <span v-if="cinema.postcode">〒{{ cinema.postcode }}</span>
            {{ cinema.prefecture || "" }}{{ cinema.address || "" }}
          </p>
          <dl class="cinema-meta">
            <div>
              <dt>地域</dt>
              <dd>{{ cinema.area || "-" }}</dd>
            </div>
            <div>
              <dt>都道府県</dt>
              <dd>{{ cinema.prefecture || "-" }}</dd>
            </div>
            <div>
              <dt>チェーン</dt>
              <dd>{{ cinema.chain || "-" }}</dd>
            </div>
          </dl>
        </div>
        <dl class="cinema-stats">
          <div>
            <dt>スクリーン</dt>
            <dd>{{ form.screens.length }}</dd>
          </div>
          <div>
            <dt>総座席数</dt>
            <dd>{{ totalSeats.toLocaleString() }}</dd>
          </div>
        </dl>
      </section>

      <!-- 営業時間ボード：全スクリーンの上映可能時間帯を 1 本の時間軸で一覧 -->
      <section v-if="form.screens.length" class="panel">
        <div class="panel-head">
          <h3>営業時間</h3>
          <span class="panel-note">行をクリックするとそのスクリーンの設定へ移動します</span>
        </div>
        <div class="board">
          <div class="board-ruler" aria-hidden="true">
            <span class="board-label" />
            <span class="board-track">
              <span class="board-nextday-label" :style="{ left: `${nextDayLeft}%` }">翌日</span>
              <span
                v-for="t in boardTicks"
                :key="t.minutes"
                class="board-tick-label"
                :style="{ left: `${t.left}%` }"
              >{{ t.label }}</span>
            </span>
          </div>
          <button
            v-for="(screen, index) in form.screens"
            :key="screen._key"
            type="button"
            class="board-row"
            @click="focusScreen(screen)"
          >
            <span class="board-label">
              <span class="board-name">{{ screen.name || `スクリーン${index + 1}` }}</span>
              <span v-if="screen.maxSeats" class="board-seats">{{ screen.maxSeats }}席</span>
            </span>
            <span class="board-track">
              <span class="board-nextday" :style="{ left: `${nextDayLeft}%` }" />
              <span
                v-for="t in boardTicks"
                :key="t.minutes"
                class="board-gridline"
                :style="{ left: `${t.left}%` }"
              />
              <span v-if="barOf(screen)" class="board-bar" :style="barOf(screen)">
                {{ screen.openTime }}–{{ screen.closeTime }}
              </span>
              <span v-else class="board-empty">時間未設定</span>
            </span>
          </button>
        </div>
      </section>

      <el-form
        ref="formRef"
        :model="form"
        label-position="top"
        class="setting-form"
        scroll-to-error
        @submit.prevent
      >
        <!-- スクリーン設定 -->
        <section class="panel">
          <div class="panel-head">
            <h3>スクリーン<span class="panel-count">{{ form.screens.length }}</span></h3>
            <span class="panel-note panel-note--grow">終了時刻は 24:00 以降を翌日として入力（26:00 = 翌 2:00、最大 30:00）</span>
            <div class="panel-actions">
              <el-button type="primary" plain size="small" @click="addScreen">スクリーンを追加</el-button>
              <!-- スクリーンだけを保存する（経営戦略は別ボタン） -->
              <el-button type="primary" size="small" :loading="savingScreens" @click="handleSaveScreens">保存</el-button>
            </div>
          </div>

          <el-empty v-if="!form.screens.length" description="スクリーンがありません。「スクリーンを追加」から登録してください" :image-size="72" />

          <div
            v-for="(screen, index) in form.screens"
            :id="`screen-${screen._key}`"
            :key="screen._key"
            class="screen-row"
          >
            <!-- 表示順と並び替え -->
            <div class="screen-order">
              <span class="screen-no">{{ index + 1 }}</span>
              <button
                type="button"
                class="order-btn"
                :disabled="index === 0"
                :aria-label="`${index + 1}番目を上へ移動`"
                @click="moveScreen(index, -1)"
              >
                <el-icon><ArrowUp /></el-icon>
              </button>
              <button
                type="button"
                class="order-btn"
                :disabled="index === form.screens.length - 1"
                :aria-label="`${index + 1}番目を下へ移動`"
                @click="moveScreen(index, 1)"
              >
                <el-icon><ArrowDown /></el-icon>
              </button>
            </div>

            <div class="screen-body">
              <div class="screen-fields">
                <el-form-item
                  label="スクリーン名"
                  required
                  :prop="`screens.${index}.name`"
                  :rules="nameRule(index)"
                >
                  <el-input v-model="screen.name" maxlength="50" placeholder="例：スクリーン1" @blur="onNameBlur" />
                </el-form-item>

                <el-form-item
                  label="最大座席数"
                  required
                  :prop="`screens.${index}.maxSeats`"
                  :rules="seatsRule(index)"
                >
                  <el-input-number
                    v-model="screen.maxSeats"
                    :min="SEATS_MIN"
                    :max="SEATS_MAX"
                    :step="1"
                    step-strictly
                    controls-position="right"
                    class="seats-input"
                  />
                </el-form-item>

                <el-form-item label="上映可能時間帯" required class="time-item">
                  <div class="time-range">
                    <el-form-item :prop="`screens.${index}.openTime`" :rules="openTimeRule(index)">
                      <el-select
                        v-model="screen.openTime"
                        filterable
                        allow-create
                        default-first-option
                        placeholder="開始"
                        class="time-select"
                        @change="onOpenTimeChange(index)"
                      >
                        <el-option v-for="t in OPEN_TIME_OPTIONS" :key="t" :label="t" :value="t" />
                      </el-select>
                    </el-form-item>
                    <span class="time-sep">〜</span>
                    <el-form-item :prop="`screens.${index}.closeTime`" :rules="closeTimeRule(index)">
                      <el-select
                        v-model="screen.closeTime"
                        filterable
                        allow-create
                        default-first-option
                        placeholder="終了"
                        class="time-select"
                      >
                        <el-option v-for="t in TIME_OPTIONS" :key="t" :label="t" :value="t" />
                      </el-select>
                    </el-form-item>
                  </div>
                </el-form-item>

                <!-- 清掃間隔の既定値（タイムチャート作成の清掃間隔の初期値） -->
                <el-form-item
                  label="清掃間隔（分）"
                  required
                  :prop="`screens.${index}.cleaningMinute`"
                  :rules="cleaningRule(index)"
                >
                  <el-input-number
                    v-model="screen.cleaningMinute"
                    :min="0"
                    :max="CLEANING_MAX"
                    :step="1"
                    step-strictly
                    controls-position="right"
                    class="seats-input"
                  />
                </el-form-item>
              </div>

              <!-- 上映可能バージョン：属性ごとに複数選択（項目内はいずれか、項目間はすべてを満たす） -->
              <div class="capability">
                <div class="capability__head">
                  <span class="capability__title">上映可能バージョン</span>
                  <span class="hint capability__hint">
                    各項目は複数選択でき、すべての項目を満たす作品バージョンを上映できます。特殊なしの作品は特殊の選択に関係なく上映できます
                  </span>
                </div>
                <div class="capability-grid">
                  <el-form-item
                    label="2D/3D"
                    required
                    :prop="`screens.${index}.dimensions`"
                    :rules="capabilityRule(index, '2D/3D')"
                  >
                    <el-select v-model="screen.dimensions" multiple placeholder="選択（複数可）" class="capability-select">
                      <el-option v-for="d in options.dimensions" :key="d" :label="d" :value="d" />
                    </el-select>
                  </el-form-item>
                  <el-form-item
                    label="上映方式"
                    required
                    :prop="`screens.${index}.screenFormats`"
                    :rules="capabilityRule(index, '上映方式')"
                  >
                    <el-select
                      v-model="screen.screenFormats"
                      multiple
                      filterable
                      placeholder="選択（複数可）"
                      class="capability-select"
                    >
                      <el-option v-for="f in options.screenFormats" :key="f" :label="f" :value="f" />
                    </el-select>
                  </el-form-item>
                  <el-form-item label="特殊（任意）">
                    <el-select v-model="screen.specials" multiple clearable placeholder="なし" class="capability-select">
                      <el-option v-for="sp in options.specials" :key="sp" :label="sp" :value="sp" />
                    </el-select>
                  </el-form-item>
                </div>
              </div>
            </div>

            <el-button class="screen-remove" type="danger" text size="small" @click="removeScreen(index)">
              削除
            </el-button>
          </div>
        </section>

        <!-- 経営戦略（自由記述） -->
        <section class="panel">
          <div class="panel-head">
            <h3>経営戦略</h3>
            <span class="panel-note panel-note--grow">空欄のままでも保存できます</span>
            <div class="panel-actions">
              <!-- 経営戦略だけを保存する（スクリーンは別ボタン） -->
              <el-button type="primary" size="small" :loading="savingStrategy" @click="handleSaveStrategy">保存</el-button>
            </div>
          </div>
          <div class="strategy-grid">
            <el-form-item v-for="f in STRATEGY_FIELDS" :key="f.key" :label="f.label">
              <el-input
                v-model="form.strategy[f.key]"
                type="textarea"
                :autosize="{ minRows: 3, maxRows: 10 }"
                :maxlength="TEXT_MAX"
                show-word-limit
                :placeholder="f.placeholder"
              />
            </el-form-item>
          </div>
        </section>
      </el-form>
    </template>
  </div>
</template>

<style lang="less" scoped>
/* 系列ページ（全作品・公開カレンダー）と同じデザイントークンを使う。深色モードは index.html 側で切り替わる */
.setting-page {
  --sp-brand: var(--brand-color, #cc251b);
  --sp-panel: var(--bg-panel, #fff);
  --sp-border: var(--border-color, #e5e5e5);
  --sp-text: var(--text-primary, #000);
  --sp-text-2: var(--text-secondary, #4a4a4a);
  --sp-muted: var(--text-tertiary, #757575);
  --sp-tint: color-mix(in srgb, var(--sp-brand) 7%, var(--sp-panel));
  --sp-tint-strong: color-mix(in srgb, var(--sp-brand) 14%, var(--sp-panel));
  --sp-radius: var(--border-radius, 6px);

  max-width: 1180px;
  padding: 0 24px 40px 15px;
  box-sizing: border-box;
  color: var(--sp-text);
}

/* ---------- 操作バー ---------- */
.page-bar {
  position: sticky;
  top: 0;
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 15px 0 12px;
  background: var(--bg-body, #fff);

  &__title {
    display: flex;
    align-items: center;
    gap: 10px;
    min-width: 0;

    h2 {
      margin: 0;
      font-size: 18px;
      font-weight: 700;
      white-space: nowrap;
    }
  }
}

.state {
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 12px;
  line-height: 18px;
  color: var(--sp-text-2);
  background: color-mix(in srgb, var(--sp-muted) 14%, var(--sp-panel));

  &--warn {
    color: var(--sp-brand);
    background: var(--sp-tint-strong);
  }
}

.first-notice {
  margin: 0 0 16px;
  padding: 10px 14px;
  border-left: 3px solid var(--sp-brand);
  border-radius: 0 var(--sp-radius) var(--sp-radius) 0;
  font-size: 13px;
  line-height: 1.7;
  color: var(--sp-text-2);
  background: var(--sp-tint);
}

/* ---------- パネル共通 ---------- */
.panel {
  margin-bottom: 16px;
  border: 1px solid var(--sp-border);
  border-radius: var(--sp-radius);
  background: var(--sp-panel);
  box-shadow: var(--shadow-sm);
  overflow: hidden;
}

.panel-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  padding: 12px 18px;
  border-bottom: 1px solid var(--sp-border);
  background: var(--sp-tint);

  h3 {
    margin: 0;
    font-size: 15px;
    font-weight: 700;
  }
}

// 見出し右側のボタン群（スクリーンを追加・保存）
.panel-actions {
  display: flex;
  align-items: center;
  gap: 8px;

  .el-button + .el-button {
    margin-left: 0;
  }
}

.panel-count {
  margin-left: 8px;
  font-weight: 500;
  font-variant-numeric: tabular-nums;
  color: var(--sp-muted);
}

.panel-note {
  font-size: 12px;
  color: var(--sp-muted);

  // 見出しとボタンの間に置くときは残り幅を取って右寄せ
  &--grow {
    flex: 1;
    text-align: right;
  }
}

.hint {
  margin: 4px 0 0;
  font-size: 12px;
  line-height: 1.6;
  color: var(--sp-muted);
}

/* ---------- 劇場基本情報 ---------- */
.cinema-panel {
  display: flex;
  align-items: stretch;
  justify-content: space-between;
}

.cinema-main {
  flex: 1;
  min-width: 0;
  padding: 18px 20px;
}

.cinema-name {
  margin: 0;
  font-size: 22px;
  font-weight: 700;
  line-height: 1.3;
  letter-spacing: 0.01em;
}

.cinema-address {
  margin: 4px 0 0;
  font-size: 13px;
  color: var(--sp-text-2);

  span {
    margin-right: 6px;
  }
}

.cinema-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 6px 28px;
  margin: 14px 0 0;

  div {
    display: flex;
    gap: 8px;
    font-size: 13px;
  }

  dt {
    color: var(--sp-muted);
  }

  dd {
    margin: 0;
    font-weight: 500;
  }
}

.cinema-stats {
  display: flex;
  margin: 0;
  border-left: 1px solid var(--sp-border);
  background: var(--sp-tint);

  div {
    display: flex;
    flex-direction: column;
    justify-content: center;
    min-width: 120px;
    padding: 14px 22px;

    & + div {
      border-left: 1px solid var(--sp-border);
    }
  }

  dt {
    font-size: 12px;
    color: var(--sp-muted);
  }

  dd {
    margin: 2px 0 0;
    font-size: 26px;
    font-weight: 700;
    line-height: 1.2;
    font-variant-numeric: tabular-nums;
  }
}

/* ---------- 営業時間ボード ---------- */
.board {
  --board-label-w: 160px;

  padding: 6px 18px 14px;
}

.board-ruler,
.board-row {
  display: grid;
  grid-template-columns: var(--board-label-w) 1fr;
  align-items: center;
  column-gap: 12px;
}

.board-ruler {
  height: 28px;

  .board-track {
    height: 100%;
    background: transparent;
  }
}

.board-tick-label,
.board-nextday-label {
  position: absolute;
  bottom: 4px;
  font-size: 11px;
  line-height: 1;
  font-variant-numeric: tabular-nums;
  color: var(--sp-muted);
  transform: translateX(-50%);
}

.board-nextday-label {
  bottom: auto;
  top: 0;
  padding-left: 6px;
  color: var(--sp-brand);
  transform: none;
}

.board-row {
  width: 100%;
  padding: 3px 0;
  border: 0;
  border-radius: 4px;
  font: inherit;
  color: inherit;
  text-align: left;
  background: transparent;
  cursor: pointer;

  &:hover .board-track {
    background: color-mix(in srgb, var(--sp-muted) 10%, var(--sp-panel));
  }

  &:hover .board-name {
    color: var(--sp-brand);
  }

  &:focus-visible {
    outline: 2px solid var(--sp-brand);
    outline-offset: 2px;
  }
}

.board-label {
  display: flex;
  align-items: baseline;
  gap: 8px;
  min-width: 0;
}

.board-name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 13px;
  font-weight: 600;
}

.board-seats {
  flex-shrink: 0;
  font-size: 12px;
  font-variant-numeric: tabular-nums;
  color: var(--sp-muted);
}

.board-track {
  position: relative;
  display: block;
  height: 26px;
  border-radius: 4px;
  background: color-mix(in srgb, var(--sp-muted) 6%, var(--sp-panel));
  transition: background-color 0.15s;
}

// 翌日帯：細い斜線で「日付をまたいだ時間」であることを示す
.board-nextday {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  border-left: 1px dashed color-mix(in srgb, var(--sp-brand) 55%, transparent);
  border-radius: 0 4px 4px 0;
  background: repeating-linear-gradient(
    -45deg,
    color-mix(in srgb, var(--sp-brand) 7%, transparent) 0 4px,
    transparent 4px 8px
  );
}

.board-gridline {
  position: absolute;
  top: 0;
  bottom: 0;
  width: 1px;
  background: var(--sp-border);
}

.board-bar {
  position: absolute;
  top: 4px;
  bottom: 4px;
  display: flex;
  align-items: center;
  min-width: 4px;
  padding: 0 8px;
  overflow: hidden;
  border-radius: 3px;
  font-size: 12px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
  color: color-mix(in srgb, var(--sp-brand) 65%, var(--sp-text));
  border-left: 3px solid var(--sp-brand);
  background: color-mix(in srgb, var(--sp-brand) 22%, var(--sp-panel));
  box-sizing: border-box;
}

.board-empty {
  position: absolute;
  top: 50%;
  left: 10px;
  font-size: 12px;
  color: var(--sp-muted);
  transform: translateY(-50%);
}

/* ---------- スクリーン行 ---------- */
.screen-row {
  display: grid;
  grid-template-columns: 40px minmax(0, 1fr) auto;
  gap: 0 16px;
  padding: 18px 18px 6px;
  scroll-margin-top: 72px;

  & + & {
    border-top: 1px solid var(--sp-border);
  }

  &:focus-within {
    background: color-mix(in srgb, var(--sp-brand) 3%, var(--sp-panel));
  }
}

.screen-order {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
}

.screen-no {
  margin-bottom: 4px;
  font-size: 20px;
  font-weight: 700;
  line-height: 1.4;
  font-variant-numeric: tabular-nums;
  color: var(--sp-brand);
}

.order-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 22px;
  padding: 0;
  border: 1px solid var(--sp-border);
  border-radius: 4px;
  color: var(--sp-text-2);
  background: var(--sp-panel);
  cursor: pointer;

  &:hover:not(:disabled) {
    border-color: var(--sp-brand);
    color: var(--sp-brand);
  }

  &:focus-visible {
    outline: 2px solid var(--sp-brand);
    outline-offset: 1px;
  }

  &:disabled {
    opacity: 0.35;
    cursor: not-allowed;
  }
}

.screen-body {
  min-width: 0;
}

.screen-fields {
  display: grid;
  // スクリーン名・最大座席数・上映可能時間帯・清掃間隔
  grid-template-columns: minmax(160px, 1.1fr) 140px minmax(280px, 1.5fr) 140px;
  gap: 0 16px;
}

.screen-remove {
  align-self: start;
  margin-top: 2px;
}

.seats-input {
  width: 100%;
}

.time-item {
  .time-range {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    width: 100%;

    // 入れ子の form-item は下余白なし
    :deep(.el-form-item) {
      flex: 1;
      margin-bottom: 0;
    }
  }

  .time-sep {
    line-height: 32px;
    color: var(--sp-muted);
  }

  .time-select {
    width: 100%;
  }
}

/* ---------- 上映可能バージョン ---------- */
.capability {
  &__head {
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    gap: 2px 12px;
    margin-bottom: 6px;
  }

  &__title {
    font-size: 14px;
    font-weight: 500;
    color: var(--el-text-color-regular);

    // 必須マーク（el-form-item の required 表示に合わせる）
    &::before {
      content: "*";
      margin-right: 4px;
      color: var(--el-color-danger);
    }
  }

  &__hint {
    width: auto;
    margin: 0;
  }
}

.capability-grid {
  display: grid;
  grid-template-columns: minmax(140px, 0.8fr) minmax(220px, 1.4fr) minmax(160px, 1fr);
  gap: 0 16px;
}

.capability-select {
  width: 100%;
}

/* ---------- 経営戦略 ---------- */
.strategy-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0 20px;
  padding: 16px 18px 4px;
}

@media (prefers-reduced-motion: reduce) {
  .board-track {
    transition: none;
  }
}

// 4 列（スクリーン名・最大座席数・上映可能時間帯・清掃間隔）は最小でも約 770px 必要なため、
// 入りきらない幅では 2 列（スクリーン名｜最大座席数 / 上映可能時間帯｜清掃間隔）にして「削除」ボタンと重ならないようにする
@media (max-width: 1360px) {
  .screen-fields {
    grid-template-columns: minmax(0, 1fr) 140px;
  }
}

@media (max-width: 768px) {
  .setting-page {
    padding: 0 12px 32px;
  }

  .cinema-panel {
    flex-direction: column;
  }

  .cinema-stats {
    border-top: 1px solid var(--sp-border);
    border-left: 0;

    div {
      flex: 1;
      min-width: 0;
      padding: 10px 16px;
    }

    dd {
      font-size: 20px;
    }
  }

  .board {
    --board-label-w: 84px;

    padding: 6px 12px 12px;
  }

  .board-seats {
    display: none;
  }

  .screen-row {
    grid-template-columns: minmax(0, 1fr) auto;
    padding: 14px 12px 4px;
  }

  // スマホでは番号と並び替えボタンを横並びにして本文の上に置く
  .screen-order {
    grid-column: 1;
    flex-direction: row;
    margin-bottom: 8px;
  }

  .screen-no {
    margin: 0 6px 0 0;
  }

  .screen-body {
    grid-column: 1 / -1;
    grid-row: 2;
  }

  .screen-remove {
    grid-column: 2;
    grid-row: 1;
  }

  .screen-fields,
  .strategy-grid {
    grid-template-columns: 1fr;
  }

  .strategy-grid {
    padding: 12px 12px 4px;
  }

  .panel-note--grow {
    flex-basis: 100%;
    order: 3;
    text-align: left;
  }

  .capability-grid {
    grid-template-columns: 1fr;
  }
}
</style>
